import { ReturnItemCard } from 'components';
import { ItemCard, LpCard } from 'components/item-verification';
import { TripContext } from 'contexts';
import { List } from 'devextreme-react/list';
import DataSource from 'devextreme/data/data_source';
import { SearchResultType } from 'enums';
import React, { FC, useContext } from 'react';
import { LicensePlate, LineItem, OrderItem, ReturnItem } from 'services/types';
import { SearchResult } from 'types';
import './search-result-list.scss';

type Props = {
    results: SearchResult<OrderItem | LicensePlate | ReturnItem>[]
    searchString?: string
    visible?: boolean
    onResultSelected?: (result: SearchResult<OrderItem | LicensePlate | ReturnItem>) => void,
    onDetailsClicked: (orderItem: OrderItem) => void
    onVerifyClicked: (orderItem: OrderItem) => void,
    onApproveChangeClicked?: (orderItem: OrderItem) => void
}

export const SearchResultList: FC<Props> = ({ results, searchString, visible = true, onResultSelected, onDetailsClicked, onVerifyClicked, onApproveChangeClicked = () => {} }) => {

    const tripContext = useContext(TripContext)

    const isValidSearch = (): boolean => (searchString != null && searchString.trim().length > 0)

    const filterResultsBySearchQuery = (): SearchResult<OrderItem | LicensePlate | ReturnItem>[] => {
        if (isValidSearch()) {
            let keywords: string[] = searchString!.trim().toLowerCase().split(' ').filter(keyword => keyword.length > 0)
            let filteredResults = [...results]

            keywords.forEach(keyword => {
                filteredResults.forEach(result => {
                    let valuesToSearch: string[] = []
                    if (result.type === SearchResultType.LicensePlate) {
                        let lp = result.value as LicensePlate
                        valuesToSearch = [lp.id.toString()]
                    }
                    else if (result.type === SearchResultType.ReturnItem) {
                        let returnItem = result.value as ReturnItem
                        const lineItem: LineItem = tripContext.lineItems.find(lineItem => lineItem.id == returnItem.lineItemId) ?? {} as LineItem
                        valuesToSearch = [
                            lineItem.longDescription ?? '',
                            lineItem.description ?? '',
                            lineItem.universalProductCode?.toString() ?? '',
                            lineItem.manufacturerItemId?.toString() ?? '',
                            lineItem.id?.toString() ?? '',
                            returnItem.id?.toString(),
                            returnItem.customerItemId ?? '',
                            returnItem.orderId?.toString() ?? '',
                            returnItem.customerItemId?.toString() ?? '',
                        ].filter(value => value.length > 0)
                    }
                    else {
                        let item = result.value as OrderItem
                        const lineItem: LineItem = tripContext.lineItems.find(lineItem => lineItem.id == item.lineItemId) ?? {} as LineItem
                        valuesToSearch = [
                            lineItem.longDescription ?? '',
                            lineItem.description ?? '',
                            item.id?.toString(),
                            item.customerItemId ?? '',
                            item.customerPoNumber ?? '',
                            item.orderId?.toString() ?? '',
                            lineItem.universalProductCode?.toString() ?? '',
                            item.customerItemId?.toString() ?? '',
                            lineItem.manufacturerItemId?.toString() ?? '',
                            lineItem.id?.toString() ?? ''
                        ].filter(value => value.length > 0)
                    }
                    valuesToSearch.forEach(value => {
                        if (value.toLowerCase().includes(keyword)) result.keywordMatches = result.keywordMatches ? result.keywordMatches + 1 : 1
                    })
                })
            })

            return keywords.length > 0 ?
                filteredResults.filter(result => result.keywordMatches && result.keywordMatches > 0)
                    .sort((a, b) => (b.keywordMatches ?? 0) - (a.keywordMatches ?? 0)) //sort by matches desc
                : filteredResults
        }
        else return results.sort((a, b) => (a.value.verifiedMethod ? 1 : 0) - (b.value.verifiedMethod ? 1 : 0))
    }

    const renderResult = (result: SearchResult<OrderItem | LicensePlate | ReturnItem>) => {
        if (result.type === SearchResultType.LicensePlate) return <LpCard lpId={result.value.id} />
        else if (result.type === SearchResultType.ReturnItem) return <ReturnItemCard returnItemId={result.value.id} />
        else return <ItemCard orderItemId={result.value.id} onDetailsClicked={onDetailsClicked} onVerifyClicked={onVerifyClicked} onApproveChangeClicked={onApproveChangeClicked} />
    }

    const filteredResults: SearchResult<OrderItem | LicensePlate | ReturnItem>[] = filterResultsBySearchQuery()
    const dataSource = new DataSource({
        store: filteredResults,
        pageSize: 10,
        paginate: true
    })

    return (
        <List
            activeStateEnabled={false}
            visible={visible}
            dataSource={dataSource}
            itemRender={renderResult}
            onItemClick={(e: any) => onResultSelected && onResultSelected(e.itemData)}
            noDataText={isValidSearch() ? `No results for "${searchString}"` : 'No Results'}
        />
    )
}